<template>
    <div>
        <!--
            Both editor runtimes ship while `editor_v4` is rolled out team by
            team. The two urls below go only to v3, which is given a separate
            endpoint for reading one autosave where v4 derives it from the
            listing url - forwarding them explicitly keeps them off v4 and out
            of the DOM. Removing the flag means deleting EditorV3.vue and
            collapsing this back to a single element.
        -->
        <EditorV4 v-if="editorV4" v-bind="shared"></EditorV4>
        <EditorV3
            v-else
            v-bind="shared"
            :get-autosave-url="getAutosaveUrl"
            :create-autosave-url="createAutosaveUrl"
        ></EditorV3>
    </div>
</template>

<script setup lang="ts">
import EditorV3 from '@components/Editor/EditorV3.vue';
import EditorV4 from '@components/Editor/EditorV4.vue';
import { IStage } from '@topol.io/editor-vue';
import { ISavedBlockData } from '@components/Editor/composables/useSavedBlocksService';

type CurrentUser = {
    id: number;
    name: string;
    profile_photo_path: string;
    profile_photo_url: string;
};

interface Props {
    apiKey: string;
    token: string;
    definition: string;
    saveUrl: string;
    backUrl: string;
    savedBlocks: ISavedBlockData[];
    saveSavedBlockUrl: string;
    getAutosaveUrl: string;
    sendTestEmailUrl: string;
    createAutosaveUrl: string;
    getAutosavesUrl: string;
    theme: string;
    fontSizes: string;
    language?: string;
    title?: string;
    currentUser: CurrentUser;
    ownerId: string | number;
    templateId: number;
    templateStatus?: string | null;
    lambdaStage: string;
    chatAI?: boolean;
    userRole?: string;
    stage?: IStage;
    getConversationsUrl?: string;
    getCommentsUrl?: string;
    enableComments?: boolean;
    getFeedsUrl?: string;
    getProductsUrl?: string;
    defaultFonts?: string;
    customFonts?: string;
    smartMergeTags?: string;
    mergeTags?: string;
    colors?: string;
    imageCompressionOptions?: string;
    enableImageToTextRatio?: boolean;
    googlePromotionalAnnotation?: boolean;
    renameTemplate?: boolean;
    htmlMinified?: boolean;
    enableSectionVariants?: boolean;
    isSubscribed?: boolean;
    testingEmails: string;
    teamUsers?: string;
    /** Whether the current team is opted into the Email Editor v4 runtime. */
    editorV4: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    language: 'en',
    title: '',
    dev: false,
    chatAI: false,
    userRole: 'owner',
    stage: 'production',
    templateStatus: null,
    getConversationsUrl: '',
    getCommentsUrl: '',
    enableComments: false,
    getFeedsUrl: '',
    getProductsUrl: '',
    defaultFonts: '[]',
    customFonts: '[]',
    smartMergeTags: '{"enabled":false}',
    mergeTags: '[]',
    colors: '[]',
    imageCompressionOptions: '[]',
    enableImageToTextRatio: false,
    googlePromotionalAnnotation: false,
    renameTemplate: false,
    htmlMinified: false,
    enableSectionVariants: false,
    isSubscribed: true,
    teamUsers: '[]',
});

// See Pages/Editor/index.vue - these three configure the runtime, not the
// template, and only v3 is given the autosave read endpoint.
const { editorV4, getAutosaveUrl, createAutosaveUrl, ...shared } = props;
</script>

<script lang="ts">
import BlankLayout from '@/Layouts/Blank.vue';

export default {
    layout: BlankLayout,
};
</script>
